import { mountDevBadge } from './devBadge';
import { bootstrapRegion } from './regionBootstrap';
import { bootstrapAuthMode, isTransparentAuth } from './auth/authMode';
import { captureLinkSecrets, redirectTicketLanding } from './auth/linkSecrets';
import { reconcileImpersonationIdentity } from './impersonationHandoff';

// First of all: an e-mailed link carries a secret in the page address (invitation, password reset,
// one-time sign-in code). It is taken out of the address before anything can record it (analytics
// are started at mount) — see ./auth/linkSecrets. A no-op on every other address.
captureLinkSecrets();
// A link opened in a tab where the app is already running only changes the address: the secret is
// taken out just the same, and the page starts over — as it does when a link opens a new tab — so
// that the screen reads the new link and nothing of whoever used the tab before stays in memory.
// (Registered before the router and analytics exist: they only ever see the cleaned address.)
window.addEventListener('hashchange', () => {
  if (captureLinkSecrets()) window.location.reload();
});

// Before anything mounts: if the impersonation token changed (a new hand-off, or
// impersonation ended), drop the previous target's cached identity so this load
// bootstraps cleanly. Without this, tabs share localStorage and the 2nd
// impersonation queries the 1st target's user/org ids → 403/404.
reconcileImpersonationIdentity();

// Shared entrypoint for both app versions (legacy API + new API V4).
//
// Which app loads at which path is controlled by REACT_APP_NEW_AT_N (default "true"):
//   true  ->  /  = legacy ,  /n = new (V4)
//   false ->  /  = new (V4),  /n = legacy        (inverted)
// Both apps use HashRouter, so `/n` is purely a path-prefix app selector; the real routes
// live in the hash and are preserved across navigation.
//
// Boundary-safe match: only the exact `n` segment, never /news, /notifications, etc.
const N_PREFIX = '/n';
const path = window.location.pathname;
const isNPath = path === N_PREFIX || path.startsWith(N_PREFIX + '/');

// Stamp the base path of the app we're about to mount ('' at root, '/n' at the /n position)
// BEFORE the app's chunk loads. Absolute-redirect sites read window.__APP_BASE__ so that
// login/logout/"go home" stay within the active app in either toggle direction. (Both apps
// sit at the current path once loaded, so the base is simply whether we're under /n.)
window.__APP_BASE__ = isNPath ? N_PREFIX : '';

// REACT_APP_NEW_AT_N (default true): does the /n path serve the NEW (V4) app? "false" inverts.
const newAtN = process.env.REACT_APP_NEW_AT_N !== 'false';
const isNew = isNPath ? newAtN : !newAtN;

// Non-prod corner badge so it's obvious at a glance which app (LEGACY vs NEW/V4) is loaded.
mountDevBadge(isNew ? 'new' : 'legacy', window.__APP_BASE__);

// Resolve which region serves this visitor BEFORE the app mounts, so the very
// first API call already carries the right `location` header instead of the old
// hardcoded 'us' fallback. Kicked off here (in parallel with the chunk download)
// and awaited just before mount: a cold start pays one bounded round trip, warm
// starts use the cached value, and any failure is swallowed so the app still
// mounts. No-ops entirely in single-region deployments. See ./regionBootstrap.
const regionReady = bootstrapRegion();

// Same idea for HOW people sign in: the API says whether this deployment redirects to Auth0 or
// runs the app's own screens (transparent authentication). Resolved before mount so the right
// provider is chosen from the first render. See ./auth/authMode.
const authModeReady = bootstrapAuthMode(isNew ? 'new' : 'legacy');

// Dynamic import so only the selected app's chunk is fetched/parsed at runtime (no
// double-instantiated providers, runtime payload stays ~1x). Do NOT statically import
// ./legacy or ./new here, or webpack folds both apps into the entry chunk.
(isNew ? import('./new/bootstrap') : import('./legacy/bootstrap')).then(async ({ mount }) => {
  await Promise.all([regionReady, authModeReady]);
  // Transparent authentication: an invitation issued through Auth0 earlier is accepted on the
  // app's own screen — the page moves there instead of mounting on the ticket's address.
  if (isTransparentAuth() && redirectTicketLanding()) return;
  mount();
});

// The service worker is app-agnostic (image-cache only, scope '/') — register once here,
// shared by both versions. Do not register a second worker from either app.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;
    navigator.serviceWorker.register(swUrl).catch(() => {
      // no-op: service worker is optional
    });
  });
}

// This file has only side effects; mark it as a module so it compiles under isolatedModules.
export {};
